import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { getUser, getVisitorId, isGuestUser } from '../helpers/helperMethods';
import useCheckMobileScreen from './useCheckMobileScreen';
import useTriggerCampaign from '../react-query-hooks/useTriggerCampaign';
import { Toast_Func } from '../helpers/toast.helper';
import {
  CURRENT_USER,
  DELIVERY_DESTINATION_ID,
  DELIVERY_ORDER_TYPE,
  GUEST_USER,
  IN_STORE_DESTINATION_ID,
  ISSUE_OFFER,
  ROUTE_LOGIN,
  TRIGGER_EVENT_SESSIONM,
  UPDATE_EMAIL,
} from '../constants';
import { socialSignIn, useAuthState } from '../context/UserAuthentication';
import { setLocation, useStoreLocation } from '../context/StoreLocation';
import { useAppDispatch, useAppSelector } from '../redux/store/store';
import useReplaceGuestUserCart from '../react-query-hooks/useReplaceGuestUserCart';
import { setShowCart } from '../redux/slices/cartSlice';

const useHandleFallBackUrls = () => {
  const location = useLocation();
  const history = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);

  const action = searchParams.get('action');
  const utm_source = searchParams.get('utm_source');
  const intent = searchParams.get("intent");
  const cartId = searchParams.get("cart_id");
  
  const { dispatch, authInfo } = useAuthState();
  const reduxDispatch = useAppDispatch();
  const { dispatch: locationDispatch, locationInfo } = useStoreLocation();

  const location_id = locationInfo.selectedStore?.id;

  const [params, setParams] = useState({});
  const [campaginAction, setCampaginAction] = useState(action);
  const [loading, setLoading] = useState<boolean>(false);

  const cart = useAppSelector((state) => state.cart);

  const user = getUser();

  const isThisMobile = useCheckMobileScreen();

  const { data: userOfferResponse, isFetching } = useTriggerCampaign(
    {
      ...params,
      logged_in_user: !isGuestUser(user) ? user?.encoded_cz_id : null,
      source: isThisMobile ? 'Mobile Web' : 'Web',
    },
    Boolean(action && Object.keys(params).length)
  );
  
  const {
    refetch: refetchReplaceGuestUserCart,
    data: guestUserReplaceCartResponse
  } = useReplaceGuestUserCart(cartId,authInfo.userId);

  useEffect(() => {
    if (!action) return;
    let paramsObject = {};
    // Iterate through all parameters and store them in an object
    searchParams.forEach((value, key) => {
      paramsObject[key] = value;
    });
    if (action === UPDATE_EMAIL) {
      setParams({
        external_email: paramsObject['external_email'],
        action: paramsObject['action'],
        is_email_verified: paramsObject['is_email_verified'],
        loyalty_user_id: paramsObject['loyalty_user_id'],
        encoded_cz_id: paramsObject['encoded_cz_id'],
        source: paramsObject['source'],
      });
    }else if (action) {
      if(authInfo.type === GUEST_USER){
        paramsObject = {...paramsObject, logged_in_user: null}
      }else{
        paramsObject = {...paramsObject, logged_in_user: user?.encoded_cz_id}
      }
      setParams(paramsObject);
    }
  }, []);

  useEffect(() => {
    if (!action) return;
    if (userOfferResponse) {
      searchParams.delete('action');
      // Replace the URL with the updated one (without reloading the page)
      history(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
    if (campaginAction === ISSUE_OFFER) {
      runIssueOfferCases();
    }else if (campaginAction === UPDATE_EMAIL) {
      runUpdateEmailCases();
    }else if(campaginAction){
    if (userOfferResponse?.code === 200 && userOfferResponse.show_toast) {
          Toast_Func({ status: true, message: userOfferResponse.message });
          setCampaginAction(null);
        }
    }
  }, [userOfferResponse]);

  useEffect(() => {
    if (utm_source == 'apple_link') {
      const paramsObject: any = {};
      // Iterate through all parameters and store them in an object
      searchParams.forEach((value, key) => {
        paramsObject[key] = value;
      });

      //Making keys on frontend side, as backend denying to correct the redirect url
      const newPayload = {
        email: paramsObject?.email,
        apple_id: paramsObject?.apple_id,
        apple_verified: 1,
        id_token: paramsObject?.['payload[id_token]'],
        code: paramsObject?.['payload[code]'],
        channel: paramsObject?.['payload[channel]'],
        type: paramsObject?.type,
        platform: isThisMobile ? "MobileWeb": "web"
      };
      searchParams.delete('utm_source');
      // Replace the URL with the updated one (without reloading the page)
      history(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
      appleSignInOnRedirectUrlInCaseOfUtmSourceKey(newPayload);
    }
  }, [utm_source]);

  useEffect(() => {
    if(intent === 'replace_cart' && isGuestUser(authInfo)){
      if(cart?.showCart) reduxDispatch(setShowCart(false))
      handleReplaceGuestCustomerCart();
    }
  },[authInfo]);

  const signInUserWithoutNewAPICall = (response: any): void => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    localStorage.setItem(
      CURRENT_USER,
      JSON.stringify({ ...response, visitor_id: getVisitorId() })
    );
  };

  const runIssueOfferCases = () => {
    if (userOfferResponse?.code === 200 && userOfferResponse.show_toast) {
      Toast_Func({ status: true, message: userOfferResponse.message });
      setCampaginAction(null);
    }
    if (
      userOfferResponse &&
      isGuestUser(user) &&
      !userOfferResponse?.customer
    ) {
      history(ROUTE_LOGIN);
      setCampaginAction(null);
    }
  };

  const runUpdateEmailCases = () => {
    if (userOfferResponse?.code === 200) {
      Toast_Func({ status: true, message: userOfferResponse.message });
      setCampaginAction(null);
    }
    if (userOfferResponse && userOfferResponse?.customer) {
      signInUserWithoutNewAPICall(userOfferResponse.customer);
      setCampaginAction(null);
    }
  };

  const appleSignInOnRedirectUrlInCaseOfUtmSourceKey = async (payload) => {
    let orderType;
    if (cart?.orderType) {
      orderType =
      cart?.orderType === DELIVERY_ORDER_TYPE
          ? DELIVERY_DESTINATION_ID
          : IN_STORE_DESTINATION_ID;
    }
    try {
      setLoading(true);
      await socialSignIn(
        dispatch,
        payload,
        'APPLE_SIGNIN',
        reduxDispatch,
        (selectedStore) => {
          setLocation(locationDispatch, selectedStore);
        },
        false,
        location_id,
        orderType
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Toast_Func({ status: false, message: 'Please try again' });
    }
  };

  const handleReplaceGuestCustomerCart = async () => {
    try {
      const res = await refetchReplaceGuestUserCart();
      if(res?.data?.status){
        reduxDispatch(setShowCart(true));
      }
    } catch (error) {
      console.log(error)
    }

  }

  return { data: userOfferResponse, loading: loading || isFetching };
};

export default useHandleFallBackUrls;
